import { RequirementFriendlyDescription } from "../requirementFriendlyDescription.js";
import { s62Part1DataSchema } from "../schemas/mhaForms/treatment/s62.js";
import { MhaStatus } from "../types/patientState/PatientState.js";
import { approvedClinicianAccreds } from "../types/user.js";
export const s62DrugCategoryOptions = [
    {
        value: "hypnoticsAndAnxiolytics",
        label: "Hypnotics and Anxiolytics",
    },
    {
        value: "drugsUsedInPsychoses",
        label: "Drugs used in psychoses and related disorders",
    },
    { value: "antidepressants", label: "Antidepressant drugs" },
    {
        value: "cnsStimulants",
        label: "CNS stimulants and drugs used for ADHD",
    },
    {
        value: "drugsForNausea",
        label: "Drugs used in nausea and vertigo",
    },
    { value: "antiEpileptic", label: "Anti-epileptic drugs" },
    {
        value: "drugsForParkinsonism",
        label: "Drugs used in Parkinsonism and related disorders",
    },
    {
        value: "drugsForSubstanceDependence",
        label: "Drugs used in substance dependence",
    },
    { value: "drugsForDementia", label: "Drugs for dementia" },
    {
        value: "drugsForObesity",
        label: "Drugs used in the treatment of obesity",
    },
    { value: "analgesics", label: "Analgesics" },
];
export const s62RouteOfAdministrationOptions = [
    { value: "oral", label: "Oral" },
    { value: "patches", label: "Patches" },
    { value: "iv", label: "IV" },
    { value: "intramuscular", label: "Intramuscular" },
    { value: "buccal", label: "Buccal" },
    { value: "rectal", label: "Rectal" },
    { value: "subcutaneous", label: "Subcutaneous" },
    { value: "topical", label: "Topical" },
];
export const s62RegularityOptions = [
    { value: "regular", label: "Regular" },
    { value: "prn", label: "PRN" },
    { value: "both", label: "Both" },
];
export const s62SectionOptions = [
    { value: "section2", label: "Section 2: Admission for assessment" },
    { value: "section3", label: "Section 3: Admission for treatment" },
    {
        value: "section17E",
        label: "Section 17E: Official Recall to Hospital",
    },
    { value: "section17F", label: "Section 17F: Revocation of CTO" },
    {
        value: "section36",
        label: "Section 36: Remand of accused person to hospital for treatment",
    },
    {
        value: "section37",
        label: "Section 37: Hospital orders without restriction",
    },
    {
        value: "section37_41",
        label: "Section 37/41: Hospital orders with restriction",
    },
    { value: "section45A", label: "Section 45A: Hospital direction" },
    { value: "section38", label: "Section 38: Interim Hospital Order" },
    {
        value: "section47",
        label: "Section 47: Removal to hospital of imprisoned persons without restriction",
    },
    {
        value: "section48",
        label: "Section 48: Removal to hospital of unsentenced prisoners without restriction",
    },
    {
        value: "section47_49",
        label: "Section 47/49: Removal to hospital of imprisoned persons with restriction",
    },
    {
        value: "section48_49",
        label: "Section 48/49: Removal to hospital of unsentenced prisoners with restriction",
    },
];
export const s62ReasonOptions = [
    { value: "threeMonthsPassed", label: "3 months has passed" },
    { value: "oneOffTreatment", label: "One-off treatment" },
    { value: "lossOfCapacity", label: "Loss of capacity" },
    { value: "withdrawalOfConsent", label: "Withdrawal of consent" },
    { value: "changeInMedication", label: "Change in medication" },
    {
        value: "s62aTreatmentFollowingRecall",
        label: "S62A Treatment following recall or revocation",
    },
    { value: "ect", label: "ECT" },
    { value: "delayInSoadAssessment", label: "Delay in SOAD assessment" },
];
// A maximum of 2 ECT treatments may be administered per form; 0 is not a
// meaningful maximum when ECT is a proposed treatment.
export const s62MaxEctTreatmentOptions = Array.from({ length: 2 }, (_, i) => ({
    value: (i + 1).toString(),
    label: (i + 1).toString(),
}));
const drugEntriesField = {
    type: "multiple-copies",
    field: "drugEntries",
    addCopyLabel: "Add medication details",
    removeCopyLabel: "Remove drug category",
    titleFn: (index) => `Drug Category ${(index + 1).toString()}`,
    fields: [
        {
            type: "dropdown",
            label: "Drug category",
            placeholder: "Select a drug category",
            valueField: "category",
            textField: "categoryText",
            options: s62DrugCategoryOptions,
        },
        {
            type: "textbox",
            field: "drugNames",
            label: "Name of Drug",
            placeholder: "Enter the drug name",
        },
        {
            type: "dropdown",
            label: "Route of administration",
            placeholder: "Select the route of administration",
            valueField: "routeOfAdministration",
            textField: "routeOfAdministrationText",
            options: s62RouteOfAdministrationOptions,
        },
        {
            type: "dropdown",
            label: "Regularity/PRN",
            placeholder: "Select the regularity",
            valueField: "regularity",
            textField: "regularityText",
            options: s62RegularityOptions,
        },
        {
            type: "label",
            showStaticLabel: true,
            label: [
                "This form should be regularly reviewed to ensure the information and associated treatment is still relevant and up to date. At a minimum this should be every 2 weeks.",
            ],
        },
        {
            type: "branch",
            field: "isAboveBnfMaximum",
            label: "Is this above the maximum BNF dosage? (Regular and PRN)",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: ["percentageOfBnfMaximum"],
                    fields: [
                        {
                            type: "textbox",
                            field: "percentageOfBnfMaximum",
                            label: "Total percentage of BNF maximum (e.g. 150)",
                            placeholder: "Enter a number over 100 for the total percentage",
                            numeric: true,
                            min: 101,
                            suffix: "%",
                        },
                    ],
                },
                {
                    fieldValue: false,
                    label: "No",
                    fieldsOwned: [],
                    fields: [],
                },
            ],
        },
    ],
};
const ectTreatmentsField = {
    type: "dropdown",
    label: "What is the maximum number of ECT treatments?",
    placeholder: "Select the maximum number of ECT treatments",
    valueField: "maxEctTreatments",
    textField: "maxEctTreatmentsText",
    options: s62MaxEctTreatmentOptions,
};
// Shared label text for the urgent-treatment criteria (a)–(d).
const CRITERIA_LABELS = {
    a: "(a) The treatment is immediately necessary to save the patient's life",
    b: "(b) The treatment (not being irreversible) is immediately necessary to prevent a serious deterioration of the patient's condition",
    c: "(c) The treatment (not being irreversible or hazardous) is immediately necessary to alleviate serious suffering by the patient",
    d: "(d) The treatment (not being irreversible or hazardous) is immediately necessary and represents the minimum interference necessary to prevent the patient from behaving violently or being a danger to themselves or to others",
};
const medicationCriteriaField = {
    type: "multi-select",
    label: "Confirm criteria for urgent medication treatment (please select at least one)",
    options: [
        { field: "medicationCriteriaA", label: CRITERIA_LABELS.a },
        { field: "medicationCriteriaB", label: CRITERIA_LABELS.b },
        { field: "medicationCriteriaC", label: CRITERIA_LABELS.c },
        { field: "medicationCriteriaD", label: CRITERIA_LABELS.d },
    ],
};
// Under Section 62 only criteria (a) and (b) authorise ECT. Criteria (c) and
// (d) are shown and selectable so clinicians are not prevented from recording
// their own judgement; the accompanying warning banner explains that (c)/(d) do
// not authorise ECT.
const ectCriteriaField = {
    type: "multi-select",
    label: "Confirm criteria for urgent ECT treatment (please select at least one)",
    options: [
        { field: "ectCriteriaA", label: CRITERIA_LABELS.a },
        { field: "ectCriteriaB", label: CRITERIA_LABELS.b },
        { field: "ectCriteriaC", label: CRITERIA_LABELS.c },
        { field: "ectCriteriaD", label: CRITERIA_LABELS.d },
    ],
};
export const s62Part1Template = {
    signing: {
        // Start/sign: a doctor (GMC) who is an AC OR a nurse-prescriber (PIN) who is
        // an AC. Exclusion is by not holding a qualifying clinical accreditation, not
        // by holding any particular one - a nurse-AC who also happens to be an AMHP
        // is still allowed. Invited guests bypass this via isGuestUser/amGuest.
        userOrTeamRequirement: {
            allOf: [
                {
                    accreditation: ["doctor", "nurse"],
                    requiresRegistrationNumber: true,
                },
                { accreditation: ["approved-clinician"] },
            ],
            teamType: [],
            friendlyDescription: RequirementFriendlyDescription.approvedClinician,
        },
        // View/invite (broader than signing): nurse, doctor, HCPC or AC can see the
        // form and invite another user to start it.
        viewAndInviteRequirement: {
            accreditation: [...approvedClinicianAccreds],
            teamType: [],
        },
    },
    formBuilderFields: [
        {
            type: "practitioner",
            label: "My name, email and address",
            field: "user",
        },
        {
            type: "readonly",
            label: "Name, date of birth and address of patient treatment is given to",
            textFn: (data) => [
                data.patient?.name ?? "",
                data.patient?.dateOfBirth ?? "",
                [data.patient?.address, data.patient?.postalCode]
                    .filter(Boolean)
                    .join(", "),
            ],
            isReadOnlyPatientInfo: true,
        },
        {
            type: "hospital-picker",
            label: "Enter name and address of the hospital in which the patient is detained",
            field: "hospital",
        },
        {
            type: "dropdown",
            label: "State section patient is subject to",
            placeholder: "Select the section the patient is detained under",
            valueField: "sectionSubjectTo",
            textField: "sectionSubjectToText",
            defaultFn: (_data, context) => {
                const status = context.patientMhaState?.state?.mhaStatus?.status;
                if (status === MhaStatus.Section3) {
                    return {
                        value: "section3",
                        label: "Section 3: Admission for treatment",
                    };
                }
                return undefined;
            },
            options: s62SectionOptions,
        },
        {
            type: "dropdown",
            label: "Reason for using Section 62",
            placeholder: "Select the reason(s) for using Section 62",
            valueField: "reasonForS62",
            textField: "reasonForS62Text",
            isMultiSelect: true,
            options: s62ReasonOptions,
        },
        {
            type: "multi-select",
            label: "Confirm proposed treatment (please select at least one)",
            options: [
                {
                    field: "treatmentMedication",
                    label: "Medication",
                    fieldsOwned: [
                        "medicationCriteriaA",
                        "medicationCriteriaB",
                        "medicationCriteriaC",
                        "medicationCriteriaD",
                        "drugEntries",
                    ],
                },
                {
                    field: "treatmentEct",
                    label: "Electroconvulsive Therapy (ECT)",
                    fieldsOwned: [
                        "ectCriteriaA",
                        "ectCriteriaB",
                        "ectCriteriaC",
                        "ectCriteriaD",
                        "maxEctTreatments",
                        "maxEctTreatmentsText",
                    ],
                },
            ],
        },
        // Medication block — shown only when medication is a chosen treatment type.
        // A single hideLabel branch acts as a pure conditional renderer gated on
        // the treatmentMedication checkbox set above.
        {
            type: "branch",
            field: "treatmentMedication",
            branches: [
                {
                    fieldValue: true,
                    label: "",
                    hideLabel: true,
                    fieldsOwned: [
                        "medicationCriteriaA",
                        "medicationCriteriaB",
                        "medicationCriteriaC",
                        "medicationCriteriaD",
                        "drugEntries",
                    ],
                    fields: [medicationCriteriaField, drugEntriesField],
                },
            ],
        },
        // ECT block — shown only when ECT is a chosen treatment type.
        {
            type: "branch",
            field: "treatmentEct",
            branches: [
                {
                    fieldValue: true,
                    label: "",
                    hideLabel: true,
                    fieldsOwned: [
                        "ectCriteriaA",
                        "ectCriteriaB",
                        "ectCriteriaC",
                        "ectCriteriaD",
                        "maxEctTreatments",
                        "maxEctTreatmentsText",
                    ],
                    fields: [
                        {
                            type: "label",
                            bannerType: "warning",
                            label: [
                                "For ECT, or medication administered as part of ECT, only the first two categories below apply. Criteria relating to (c) or (d) do not authorise ECT under Section 62.",
                            ],
                        },
                        ectCriteriaField,
                        ectTreatmentsField,
                    ],
                },
            ],
        },
        {
            type: "branch",
            field: "isLongTermTreatment",
            label: "Do you expect this treatment to continue long term?",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: [
                        "soadContactStatus",
                        "soadContactDate",
                        "soadRequestNumber",
                        "soadNotContactedReasons",
                    ],
                    fields: [
                        {
                            type: "label",
                            bannerType: "warning",
                            label: [
                                "Please ensure that a Second Opinion Appointed Doctor (SOAD) has been, or will be, requested from the CQC.",
                            ],
                        },
                        {
                            type: "branch",
                            field: "soadContactStatus",
                            label: "Please confirm which of the following apply",
                            branches: [
                                {
                                    fieldValue: "contacted",
                                    label: "I confirm I have already made a request to the CQC for a second opinion (SOAD)",
                                    fieldsOwned: ["soadContactDate", "soadRequestNumber"],
                                    fields: [
                                        {
                                            type: "date",
                                            field: "soadContactDate",
                                            label: "Please provide the date you made a request to the CQC for a second opinion (SOAD)",
                                            maximum: (today) => today.endOf("day"),
                                        },
                                        {
                                            type: "textbox",
                                            field: "soadRequestNumber",
                                            label: "SOAD request number (optional)",
                                            placeholder: "Enter the SOAD request number",
                                        },
                                    ],
                                },
                                {
                                    fieldValue: "not-contacted",
                                    label: "I confirm that I will make a request to the CQC for a second opinion (SOAD) at the soonest opportunity",
                                    fieldsOwned: ["soadNotContactedReasons"],
                                    fields: [
                                        {
                                            type: "textbox",
                                            field: "soadNotContactedReasons",
                                            label: "Please describe exceptional reasons for not doing so and when you expect to contact a SOAD",
                                            placeholder: "Enter the exceptional reasons and when you expect to contact a SOAD",
                                            rows: "5",
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    fieldValue: false,
                    label: "No",
                    fieldsOwned: [],
                    fields: [],
                },
            ],
        },
        {
            type: "approved-clinician-confirmation-checkbox",
            label: "I am the approved clinician for treatment of this patient",
            checkboxLabel: "Please confirm",
            tooltip: "An approved clinician is a mental health professional approved under the Mental Health Act 1983 as having the competencies required to take on the role of approved clinician. If you are unsure whether you are an Approved Clinician, check with your mental health law team.",
        },
    ],
    dataSchema: s62Part1DataSchema,
};
export const s62V1Base = {
    id: "mha-s62",
    category: "treatment",
    version: "1.0",
    status: "active",
    formName: "S62",
    section: "62/62A",
    description: "Urgent Treatment",
    displayTitle: "Urgent Treatment: Section 62/62A",
    parts: [s62Part1Template],
    extractGenericData: (data) => ({
        hospital: data[0]?.data.hospital,
    }),
};
//# sourceMappingURL=s62.js.map